import React from 'react';
import { Link } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { Download, Share2, Copy, ChevronLeft, Smartphone } from 'lucide-react';

const APP_URL = 'https://misyou.in';

export default function QRCodePage() {
  const [copied, setCopied] = React.useState(false);

  const copyLink = async () => {
    try {
      await navigator.clipboard.writeText(APP_URL);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch (e) {
      // Clipboard API can be blocked (insecure origin, permissions, older browsers).
      // Non-fatal: user can still copy manually from the QR link shown on-screen.
      console.warn('[qr] clipboard.writeText failed:', e?.message || e);
    }
  };

  const share = async () => {
    if (navigator.share) {
      try {
        await navigator.share({
          title: 'MISYOU — Find Missing Persons',
          text: 'Scan or open MISYOU to report and search for missing persons.',
          url: APP_URL,
        });
      } catch (e) {
        // AbortError = user dismissed the share sheet (expected, silent).
        // Anything else = real failure worth logging.
        if (e?.name !== 'AbortError') {
          console.warn('[qr] navigator.share failed:', e?.message || e);
        }
      }
    } else {
      copyLink();
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 via-white to-blue-50 py-10 px-4">
      <div className="max-w-3xl mx-auto">
        <Link to="/" className="inline-flex items-center text-slate-600 hover:text-primary mb-6">
          <ChevronLeft className="w-4 h-4 mr-1" /> Back to home
        </Link>

        <div className="text-center mb-10">
          <h1 className="text-4xl sm:text-5xl font-black text-slate-900 mb-3">
            Share MISYOU with anyone
          </h1>
          <p className="text-base text-slate-600 max-w-xl mx-auto">
            Scan this QR with any phone camera to instantly open the MISYOU app. Perfect for posters,
            flyers, business cards, or sharing in WhatsApp.
          </p>
        </div>

        <Card className="bg-white shadow-2xl shadow-slate-200/50 border-slate-100 overflow-hidden">
          <CardContent className="p-8 sm:p-12">
            <div className="grid md:grid-cols-2 gap-8 items-center">
              {/* QR */}
              <div className="flex flex-col items-center">
                <div
                  className="bg-white p-4 rounded-2xl border-4 border-slate-900 shadow-xl"
                  data-testid="qr-image-wrapper"
                >
                  <img
                    src="/qr-misyou.png"
                    alt="MISYOU QR Code"
                    className="w-full max-w-[280px] h-auto"
                    data-testid="qr-image"
                  />
                </div>
                <p className="mt-4 text-sm text-slate-500 font-mono">{APP_URL}</p>
              </div>

              {/* Actions */}
              <div className="space-y-4">
                <div className="flex items-center gap-3 p-4 bg-blue-50 rounded-xl">
                  <Smartphone className="w-5 h-5 text-primary shrink-0" />
                  <p className="text-sm text-slate-700">
                    Open the camera app on any phone and point it at the QR code. The app will open in your browser.
                  </p>
                </div>

                <a
                  href="/qr-misyou.png"
                  download="misyou-qr-code.png"
                  className="block"
                  data-testid="download-png-link"
                >
                  <Button className="w-full justify-start" size="lg">
                    <Download className="w-4 h-4 mr-2" />
                    Download as PNG (for digital use)
                  </Button>
                </a>

                <a
                  href="/qr-misyou.svg"
                  download="misyou-qr-code.svg"
                  className="block"
                  data-testid="download-svg-link"
                >
                  <Button variant="outline" className="w-full justify-start" size="lg">
                    <Download className="w-4 h-4 mr-2" />
                    Download as SVG (for print, any size)
                  </Button>
                </a>

                <Button
                  variant="outline"
                  className="w-full justify-start"
                  size="lg"
                  onClick={share}
                  data-testid="share-link-btn"
                >
                  <Share2 className="w-4 h-4 mr-2" />
                  Share link
                </Button>

                <Button
                  variant="ghost"
                  className="w-full justify-start"
                  size="lg"
                  onClick={copyLink}
                  data-testid="copy-link-btn"
                >
                  <Copy className="w-4 h-4 mr-2" />
                  {copied ? 'Copied!' : 'Copy link'}
                </Button>
              </div>
            </div>

            <div className="mt-10 pt-6 border-t border-slate-100 text-center">
              <p className="text-xs text-slate-500">
                <strong>Print tip:</strong> Use the SVG file when printing posters or large flyers — it stays crisp at any size.
                Test the scan from ~30cm away with two different phones before printing in bulk.
              </p>
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
